import { apiClient } from '@/services/apiClient';
import type {
  StandardReportDescriptor,
  StandardReportOrder,
  StandardReportOrderInput,
  StandardReportParams,
  StandardReportRun,
  StandardReportRunStatusPoll,
} from '@/types/standardReports.types';

/**
 * Standard Reports API.
 *
 * Every endpoint here is one that exists — verified against `routes/api.php`.
 * That is worth stating because this file replaces one which declared ten
 * endpoints that never did, and which was written from documentation rather
 * than from the running application.
 *
 * NO METHOD HERE RETURNS A DOWNLOAD TOKEN. The mailed link's token is minted in
 * the worker and handed to delivery in memory; it is never in an API response,
 * so there is nothing for this layer to hold or leak.
 */

/** The V5 envelope: `{ success, data, meta }`. */
interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export const standardReportsService = {
  /**
   * The reports this caller may see — already filtered server-side by the
   * allow-list and the caller's user level. The client never decides what is
   * offered, so a report withheld by configuration cannot be reached by
   * calling the run endpoint directly either.
   */
  async catalog(): Promise<StandardReportDescriptor[]> {
    const { data } = await apiClient.get<Envelope<StandardReportDescriptor[]>>(
      '/reports/standard/catalog',
    );
    return data.data ?? [];
  },

  /**
   * Queues a run and returns its reference immediately.
   *
   * There is no synchronous variant. Every run is queued because the gateway
   * request cap is a hard limit, so callers always poll — there is no "small
   * report" fast path that could quietly stop working on a wider window.
   */
  async run(reportKey: string, params: StandardReportParams): Promise<StandardReportRun> {
    const { data } = await apiClient.post<Envelope<StandardReportRun>>(
      `/reports/standard/${encodeURIComponent(reportKey)}/run`,
      { params },
    );
    return data.data;
  },

  /**
   * `orderId` narrows to one saved order's runs. `reportKey` cannot substitute
   * for it — two orders for the same report would collapse into one history.
   */
  async listRuns(
    options: { reportKey?: string; orderId?: number; limit?: number } = {},
  ): Promise<StandardReportRun[]> {
    const { data } = await apiClient.get<Envelope<StandardReportRun[]>>('/reports/standard/runs', {
      params: options,
    });
    return data.data ?? [];
  },

  async getRun(runId: number): Promise<StandardReportRun> {
    const { data } = await apiClient.get<Envelope<StandardReportRun>>(
      `/reports/standard/runs/${runId}`,
    );
    return data.data;
  },

  async getRunStatus(runId: number): Promise<StandardReportRunStatusPoll> {
    const { data } = await apiClient.get<Envelope<StandardReportRunStatusPoll>>(
      `/reports/standard/runs/${runId}/status`,
    );
    return data.data;
  },

  // ── Standing orders ─────────────────────────────────────────────────────
  //
  // An order carries a date PRESET KEY, never resolved bounds. The server
  // re-resolves it on each fire so every delivery covers the period that just
  // closed; sending bounds would freeze a recurring report on one window.

  async listOrders(): Promise<StandardReportOrder[]> {
    const { data } = await apiClient.get<Envelope<StandardReportOrder[]>>(
      '/reports/standard/orders',
    );
    return data.data ?? [];
  },

  async createOrder(input: StandardReportOrderInput): Promise<StandardReportOrder> {
    const { data } = await apiClient.post<Envelope<StandardReportOrder>>(
      '/reports/standard/orders',
      input,
    );
    return data.data;
  },

  async updateOrder(orderId: number, input: StandardReportOrderInput): Promise<StandardReportOrder> {
    const { data } = await apiClient.put<Envelope<StandardReportOrder>>(
      `/reports/standard/orders/${orderId}`,
      input,
    );
    return data.data;
  },

  async deleteOrder(orderId: number): Promise<void> {
    await apiClient.delete(`/reports/standard/orders/${orderId}`);
  },

  async pauseOrder(orderId: number): Promise<StandardReportOrder> {
    const { data } = await apiClient.post<Envelope<StandardReportOrder>>(
      `/reports/standard/orders/${orderId}/pause`,
    );
    return data.data;
  },

  async resumeOrder(orderId: number): Promise<StandardReportOrder> {
    const { data } = await apiClient.post<Envelope<StandardReportOrder>>(
      `/reports/standard/orders/${orderId}/resume`,
    );
    return data.data;
  },

  /** Queues one extra run. Deliberately does not move the schedule. */
  async runOrderNow(orderId: number): Promise<{ runId: number }> {
    const { data } = await apiClient.post<Envelope<{ runId: number }>>(
      `/reports/standard/orders/${orderId}/run-now`,
    );
    return data.data;
  },

  /**
   * Downloads through the AUTHENTICATED route, which applies the same
   * visibility predicate and minimum user level as every other read.
   *
   * Deliberately not the token route: that one exists for mailed links to
   * people with no session, and using it from an authenticated client would
   * consume the link's limited use count for no benefit.
   */
  async downloadRun(runId: number): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(`/reports/standard/runs/${runId}/download`, {
      responseType: 'blob',
    });
    return data;
  },
};

/**
 * Saves a downloaded blob under a filename.
 *
 * Kept here rather than inline in a page so the object URL is always revoked —
 * leaving them attached is a slow leak that only shows up after a user has
 * downloaded a dozen reports in one session.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
